import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetHeader,
} from 'components/ModalSheet'
import React, { useEffect, useMemo } from 'react'
import {
  CustomSelect,
  ItemSource,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import truncate from 'lodash/truncate'
import { FormGroup, FormRow } from 'components/inputs'
import cx from 'classnames'
import Icon from 'components/Icon'
import styles from './TwilioVoiceCallSettingsModal.sass'
import TwilioVoiceCallSettingsModalViewModel from './TwilioVoiceCallSettingsModalViewModel'
import { animated, useSprings } from 'react-spring'
import { lerp } from 'utils/math-util'
import Button from 'components/Button'

interface Props {
  vm: TwilioVoiceCallSettingsModalViewModel
}

export default observer(function TwilioVoiceCallSettingsModal({ vm }: Props) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={() => vm.hide()}>
      <ModalSheetHeader withCloseButton></ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>Audio Settings</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Test your audio settings for calls
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <FormGroup label="Microphone">
            <RenderMediaSelect
              selectMediaDevice={vm.setAudioInputDevice}
              devices={vm.inputDeviceList}
              selectedDevice={vm.selectedAudioInputDevice}
              icon={
                <Icon style={{ color: 'var(--grey500)' }} name="call-mic-on" />
              }
            />
            {vm.canAnalyzeOutput ? <MicrophoneCheck vm={vm} /> : null}
          </FormGroup>
          <FormRow />
          <FormGroup label="Speaker">
            <div className={styles.speakerSelectFormGroup}>
              <RenderMediaSelect
                className={styles.speakerSelect}
                disabled={!vm.canSetOutput}
                selectMediaDevice={vm.setAudioOutputDevice}
                devices={vm.outputDeviceList}
                selectedDevice={vm.selectedAudioOutputDevice}
                icon={
                  <Icon
                    style={{ color: 'var(--grey500)' }}
                    name="call-speakers"
                  />
                }
              />
              {vm.canTestOutput ? (
                <Button
                  className={styles.testOutButton}
                  type="secondary"
                  disabled={vm.testOutput.pending}
                  onClick={vm.testOutput}
                >
                  {vm.testOutput.pending ? 'Testing' : 'Test'}
                </Button>
              ) : null}
            </div>
          </FormGroup>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock />
      </ModalSheetContent>
    </ModalSheet>
  )
})

interface MediaSelectProps {
  selectMediaDevice: (device?: MediaDeviceInfo) => Promise<void>
  devices: Array<MediaDeviceInfo>
  selectedDevice?: MediaDeviceInfo | null
  icon?: null | undefined | React.ReactNode
  className?: string
  disabled?: boolean
}

const RenderMediaSelect = observer(function RenderMediaSelect({
  selectMediaDevice,
  devices,
  selectedDevice,
  icon,
  disabled,
  className,
}: MediaSelectProps) {
  const itemSource = React.useMemo(() => ItemSource.static(devices), [devices])
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })
  return (
    <div className={cx(className)}>
      <CustomSelect
        {...selectProps}
        disabled={disabled}
        icon={<Icon name="dropdown" />}
        value={selectedDevice ?? undefined}
        onChange={(device: MediaDeviceInfo | undefined) =>
          selectMediaDevice(device)
        }
        itemToString={(device) => truncate(device.label, { length: 50 })}
        renderItem={(itemProps) => (
          <SingleItemSelectedDefault
            {...itemProps}
            item={itemProps}
            itemToString={(i) => String(i.item.label)}
            visual={icon}
          />
        )}
        multi={false}
      />
    </div>
  )
})

interface MicrophoneCheckProps {
  vm: TwilioVoiceCallSettingsModalViewModel
}

const numOvals = 32

const MicrophoneCheck = observer(function MicrophoneCheck({
  vm,
}: MicrophoneCheckProps) {
  const { grey300, green400 } = useMemo(() => {
    const bodyStyles = getComputedStyle(document.body)
    const grey300 = bodyStyles.getPropertyValue('--grey300')
    const green400 = bodyStyles.getPropertyValue('--green400')

    return { grey300, green400 }
  }, [])

  const [springs, api] = useSprings(numOvals, () => ({
    from: {
      color: grey300,
    },
    to: {
      color: green400,
    },
  }))

  useEffect(() => {
    function handleVolume(inputVolume: number) {
      const mappedVol = Math.floor(lerp(0, numOvals, inputVolume))
      api.start((i) => {
        if (i > mappedVol) {
          return {
            color: grey300,
          }
        }

        return {
          color: green400,
          immediate: true,
        }
      })
    }

    vm.device?.audio?.on('inputVolume', handleVolume)
    return () => {
      vm.device?.audio?.off('inputVolume', handleVolume)
    }
  }, [vm.device?.audio, api, springs])

  return (
    <div className={styles.audioLevelVizRoot}>
      <div
        style={{
          display: 'grid',
          justifyItems: 'center',
          gridTemplateColumns: `repeat(${numOvals}, 1fr)`,
        }}
      >
        {springs.map(({ color }, index) => {
          return (
            <animated.div
              key={index}
              style={{
                display: 'inline-block',
                borderRadius: '100%',
                width: 10,
                height: 10,
                background: color,
              }}
            />
          )
        })}
      </div>
    </div>
  )
})
